import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { Alert } from "components/bootstrap";
import { Icon, Spinner } from "components/common";
import generateId from "logic/generateId";
import useSearchExecutors from "views/components/contexts/useSearchExecutors";
import Search from "views/logic/search/Search";
import createSearch from "views/logic/slices/createSearch";
import SearchExecutionState from "views/logic/search/SearchExecutionState";
import Query, { newFiltersForQuery } from "views/logic/queries/Query";
const GEO_COORDINATES_FIELD = "source_geo_coordinates";
const constructFieldCheckSearch = (userField, query, streams, streamCategories, filters, lookbackMs, searchTypeId, queryId) => {
  var _a;
  const existsQuery = `_exists_:${GEO_COORDINATES_FIELD} AND _exists_:${userField}`;
  const fullQuery = (query == null ? void 0 : query.trim()) ? `(${query.trim()}) AND ${existsQuery}` : existsQuery;
  const rangeInSeconds = Math.max(Math.floor(lookbackMs / 1e3), 1);
  const builtQuery = Query.builder().id(queryId).query({ type: "elasticsearch", query_string: fullQuery }).timerange({ type: "relative", range: rangeInSeconds }).filter((_a = newFiltersForQuery(streams, streamCategories)) != null ? _a : null).filters(filters).searchTypes([
    {
      id: searchTypeId,
      type: "messages",
      limit: 1,
      offset: 0,
      filter: void 0,
      filters: void 0,
      name: void 0,
      query: void 0,
      timerange: void 0,
      streams: [],
      stream_categories: [],
      sort: [],
      decorators: []
    }
  ]).build();
  return Search.create().toBuilder().queries([builtQuery]).build();
};
const ImpossibleTravelFieldCheck = ({
  userField,
  query = null,
  streams,
  streamCategories = [],
  filters = [],
  lookbackMs
}) => {
  var _a, _b, _c, _d, _e, _f, _g, _h;
  const queryId = useRef(generateId()).current;
  const searchTypeId = useRef(generateId()).current;
  const [results, setResults] = useState();
  const [hasSearched, setHasSearched] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const { startJob, executeJobResult } = useSearchExecutors();
  useEffect(() => {
    if (!(userField == null ? void 0 : userField.trim())) return void 0;
    let cancelled = false;
    const timer = setTimeout(() => {
      setHasSearched(false);
      setSearchFailed(false);
      const search = constructFieldCheckSearch(
        userField,
        query,
        streams,
        streamCategories,
        filters,
        lookbackMs,
        searchTypeId,
        queryId
      );
      createSearch(search).then((createdSearch) => startJob(createdSearch, [searchTypeId], SearchExecutionState.empty())).then((jobIds) => executeJobResult({ jobIds })).then((result) => {
        if (!cancelled) {
          setResults(result);
          setHasSearched(true);
        }
      }).catch(() => {
        if (!cancelled) {
          setResults(void 0);
          setSearchFailed(true);
          setHasSearched(true);
        }
      });
    }, 500);
    return () => {
      clearTimeout(timer);
      cancelled = true;
    };
  }, [
    userField,
    query,
    streams,
    streamCategories,
    filters,
    lookbackMs,
    startJob,
    executeJobResult,
    queryId,
    searchTypeId
  ]);
  if (!(userField == null ? void 0 : userField.trim())) {
    return null;
  }
  const searchResult = (_c = (_b = (_a = results == null ? void 0 : results.result) == null ? void 0 : _a.forId(queryId)) == null ? void 0 : _b.searchTypes) == null ? void 0 : _c[searchTypeId];
  const errors = (_e = (_d = results == null ? void 0 : results.result) == null ? void 0 : _d.errors) != null ? _e : [];
  const hasError = searchFailed || errors.length > 0;
  const isFetching = !hasSearched;
  const totalResults = (_h = (_g = searchResult == null ? void 0 : searchResult.total_results) != null ? _g : (_f = searchResult == null ? void 0 : searchResult.messages) == null ? void 0 : _f.length) != null ? _h : 0;
  const hasMessages = totalResults > 0;
  if (isFetching) {
    return /* @__PURE__ */ React.createElement(Alert, { bsStyle: "info" }, /* @__PURE__ */ React.createElement(Spinner, { text: "Checking for messages with required fields..." }));
  }
  if (hasError) {
    return /* @__PURE__ */ React.createElement(Alert, { bsStyle: "warning" }, /* @__PURE__ */ React.createElement(Icon, { name: "warning" }), " Unable to check for required fields. Verify your query and stream configuration.");
  }
  if (hasMessages) {
    return /* @__PURE__ */ React.createElement(Alert, { bsStyle: "success" }, /* @__PURE__ */ React.createElement(Icon, { name: "check" }), " Found messages with the required fields (", /* @__PURE__ */ React.createElement("code", null, userField), " and", " ", /* @__PURE__ */ React.createElement("code", null, GEO_COORDINATES_FIELD), ") in the selected streams.");
  }
  return /* @__PURE__ */ React.createElement(Alert, { bsStyle: "warning" }, /* @__PURE__ */ React.createElement(Icon, { name: "warning" }), " No messages found with both ", /* @__PURE__ */ React.createElement("code", null, userField), " and", " ", /* @__PURE__ */ React.createElement("code", null, GEO_COORDINATES_FIELD), " fields in the selected streams within the lookback period. Ensure your data contains geo-enriched login events with these fields for impossible travel detection to work.");
};
export default ImpossibleTravelFieldCheck;
